
import React, { JSX } from 'react';
import { RegisterOptions } from 'react-hook-form';

import { ID as SharedID, UUID as SharedUUID, ISODate as SharedISODate, ISODateOnly as SharedISODateOnly, CorrelationId as SharedCorrelationId, JsonMap as SharedJsonMap } from 'kmstack-shared';
import { z } from 'zod';
import type * as SharedAccounting from 'kmstack-shared/apps/contracts/accounting';
import {
    KmstackABPartySchema as SharedKmstackABPartySchema,
    ExpenseSchema as SharedExpenseSchema,
    ProductSchema as SharedProductSchema,
    BillItemSchema as SharedBillItemSchema,
    BillSchema as SharedBillSchema,
    PurchaseOrderItemSchema as SharedPurchaseOrderItemSchema,
    PurchaseOrderSchema as SharedPurchaseOrderSchema,
} from 'kmstack-shared/apps/contracts/accounting';
import { PlanType } from 'kmstack-shared/core';
import type {
  AuditOutcome as SharedAuditOutcome,
  EmployeeStatus as SharedEmployeeStatus,
  ExpenseTransactionType as SharedExpenseTransactionType,
  PaymentTxType as SharedPaymentTxType,
  ReconciliationStatus as SharedReconciliationStatus,
  SettlementStatus as SharedSettlementStatus,
  TransactionStatus as SharedTransactionStatus,
  Invoice as SharedInvoice,
} from 'kmstack-shared/apps/contracts/accounting';
import {
  EPaymentFrequencyType,
  InvoiceStatusEnum,
  InvoicePaymentStatusEnum,
  PurchaseOrderStatusEnum,
  BasePaymentMethod,
  PayrollFilingStatus,
  PayrollResidencyStatus,
  PayrollTaxLayer,
  ReceiptProcessingJobStatus,
  VATFilingStatus,
} from 'kmstack-shared/apps/contracts/accounting';

export {
  EPaymentFrequencyType,
  InvoiceStatusEnum,
  InvoicePaymentStatusEnum,
  PurchaseOrderStatusEnum,
  BasePaymentMethod,
  PayrollFilingStatus,
  PayrollResidencyStatus,
  PayrollTaxLayer,
  ReceiptProcessingJobStatus,
  VATFilingStatus,
};

////////////////////////////////////////  DEFINITION START ////////////////////////////////////////
export type ID = SharedID;
export type UUID = SharedUUID;
export type ISODate = SharedISODate;
export type ISODateOnly = SharedISODateOnly;
export type CorrelationId = SharedCorrelationId;
export type JsonMap = SharedJsonMap;

//////////////////////////////////////// DEFINITION END ////////////////////////////////////////


//////////////////////////////////////// TYPES START ////////////////////////////////////////


export type DocumentIssueType = typeof EDocumentIssueStatus[keyof typeof EDocumentIssueStatus]
export type AuditOutcome = SharedAuditOutcome;
export type SettlementStatus = SharedSettlementStatus;
export type ReconciliationStatus = SharedReconciliationStatus;
export type PaymentTxType = SharedPaymentTxType;

// TABS
export type ClientSupplierFields = SharedAccounting.ClientSupplierFields;
export type ExpenseSubTab = 'expenses' | 'bills' | 'reports';
export type InventoryManagementSubTabs = 'products' | 'services';
export type IntegrationSubTab = 'webhooks' | 'api' | 'marketplace';
export type TimeTrackingSubTab = 'timer' | 'projects' | 'time-sheets' | 'invoicing';
export type InventoryTab = 'management' | 'purchase-orders' | 'reports';
export type ReportCategory = typeof EReportCategory[keyof typeof EReportCategory];
export type PayrollProcessingSubTab = typeof EPayrollProcessingSubTab[keyof typeof EPayrollProcessingSubTab];
export type PaymentViewSubTab = 'upcoming-payrolls' | 'crypto' | 'partial-bnpl';
export type Page = 'landing' | 'signup' | 'login' | 'twoFactorAuth' | 'loading' | 'dashboard';
export type ClientSupplierSubTab = 'client' | 'supplier'; // | 'price-lists' | 'credit-notes';
export type BareAutomationSubTabs = 'smart-payment-reminders' | 'auto-categorization' | 'tax-reports';
export type SecurityComplianceSubTab = 'data-security' | 'compliance-privacy' | 'dashboard' | 'payment-security';
export type InventoryReportSubTab = 'stock-valuation' | 'category-breakdown' | 'stock-levels' | 'products-vs-services' | 'turnover-rate' | 'stock-age' | 'reorder-analysis' | 'stockout-risk';
export type SettingsViewTabs = 'invoice-settings' | 'payment-methods' | 'notification' | 'user-roles-permissions' | 'transaction-settings';
export type EmployeeSubTabs = 'users' | 'time-tracking' | 'leave-approvals' | 'payroll-processing' | 'payslips' | 'employee-portal' | 'payroll-settings';
export type DashboardView = typeof DashboardViewTabs[keyof typeof DashboardViewTabs];


// PAYMENT
export type PayslipPaymentMethod = SharedAccounting.ExpenseTransactionMethod;
export type ExpenseTransactionType = SharedExpenseTransactionType;
export type TransactionStatus = SharedTransactionStatus;
export type ExpenseTransactionMethod = SharedAccounting.ExpenseTransactionMethod;
export type TransactionMethod = SharedAccounting.TransactionMethod;
export type CryptoTransactionType = SharedAccounting.CryptoTransactionType;
/** `Not sent` is used for draft invoices so that it is not calculated in the payment status,  */
export type InvoicePaymentStatus = SharedAccounting.InvoicePaymentStatus;

// STATUS
export type EmployeeStatus = SharedEmployeeStatus;
export type PayrollStatus = typeof EPayrollStatus[keyof typeof EPayrollStatus];
export type BankSyncStatus = 'In Progress' | 'Successfully' | 'Failed' | 'Not synced';
export type InvoiceStatus = SharedAccounting.InvoiceStatus;
export type PurchaseOrderStatus = SharedAccounting.PurchaseOrderStatus;
export type BillStatus = 'Paid' | 'Unpaid' | 'Overdue' | 'Draft' | 'Partially Paid' | 'Approved';

// GENERAL
export type ServiceStatus = Extract<SharedEmployeeStatus, 'Active' | 'Inactive'>;
export type PartialPaymentStatus = Extract<SharedTransactionStatus, 'Paid' | 'Pending'> | 'Overdue';
export type TimeEntryStatus = 'Tracked' | 'Billed' | 'Invoiced' | 'Draft';
export type TaxComplianceStatus = 'Compliant' | 'Non Compliant' | 'Pending';
export type ProductStatus = 'In stock' | 'Low stock' | 'Out of stock'; // Types for Inventory
// these may not be needed, we will see TODO:
export type EmploymentType = SharedAccounting.EmploymentType;
export type PayslipStatusType = 'Pending' | 'Processed' | 'Paid' | 'Reversed';
export type BaseGenericPolicyType = typeof ExtendedPolicyType[keyof typeof ExtendedPolicyType]
export type PaymentFrequencyType = SharedAccounting.PaymentFrequencyType;
export type RecurringInterval = SharedAccounting.RecurringInterval;
export type DepartMentType = SharedAccounting.DepartMentType;


export type PayrollSettingsType = typeof PayrollSettings[keyof typeof PayrollSettings];

//////////////////////////////////////// TYPES END ////////////////////////////////////////


//////////////////////////////////////// ENUM START ////////////////////////////////////////

export enum EReportCategory {
    FINANCIAL_TAX = 'financialTax',
    OPERATIONS = 'operations',
    ANALYTICS = 'analytics',
}

export enum PayrollSettings {
  SALARY_INCREASE = 'Salary Increases',
  TAX_DEDUCTION = 'Tax Brackets',
  TAX_FORMULA = 'Payroll Tax Configurations',
  LEAVE_POLICY = 'Leave Policies',
  PAY_COMPONENT = 'Pay Component',
  TAX_RELIEF = 'Tax Reliefs',
  EARNINGS_BENEFITS = 'Earnings & Benefits',
  PAYSLIP_POLICY = 'Payslip Policy',
  LEAVE_BALANCES_ABSENCES = 'Leave Balances & Absences',
}

export enum PayrollURLRestoreAction {
  ADD_SALARY_INCREASE = 'add-salary-increase',
  EDIT_SALARY_INCREASE = 'edit-salary-increase',
  ADD_TAX_BRACKET = 'add-tax-bracket',
  EDIT_TAX_BRACKET = 'edit-tax-bracket',
  ADD_TAX_RELIEF = 'add-tax-relief',
  EDIT_TAX_RELIEF = 'edit-tax-relief',
  ADD_LEAVE = 'add-leave',
  EDIT_LEAVE = 'edit-leave',
  ADD_PAY_COMPONENT = 'add-pay-component',
  EDIT_PAY_COMPONENT = 'edit-pay-component',
}

export enum DashboardViewTabs {
  OVERVIEW = 'overview',
  INVOICES = 'invoices',
  CLIENTS_SUPPLIERS = 'clients-suppliers',
  WORKFORCE = 'workforce',
  EMPLOYEE_PORTAL = 'employee-portal',
  PAYMENTS = 'payments',
  PURCHASES = 'purchases',
  INVENTORY = 'inventory',
  REPORT_ANALYTICS = 'report-analytics',
  AUTOMATION = 'automation',
  SECURITY_COMPLIANCE = 'security-compliance',
  INTEGRATIONS = 'integrations',
  SETTINGS = 'settings',
}

export enum GenericPolicyType {
  DRAFT = 'Draft',
  PENDING = 'Pending',
  ACTIVE = 'Active',
  INACTIVE = 'Inactive',
}
export enum ExtendedPolicyType {
  DRAFT = 'Draft',
  PENDING = 'Pending',
  ACTIVE = 'Active',
  INACTIVE = 'Inactive',
  SCHEDULED = 'Scheduled',
  APPLIED = 'Applied',
  REVIEW = "REVIEW",
  IN_REVIEW = "IN_REVIEW",
}

export enum EPayrollStatus {
  DRAFT = 'Draft',
  PENDING = 'Pending',
  APPROVED = 'Approved',
  PROCESSED = 'Processed',
  FAILED = 'Failed',
  REVERSED = 'Reversed',
}

export enum EPayrollPaymentStatus {
  UNPAID = 'Unpaid',
  PAID = 'Paid',
  FAILED = 'Failed',
  VOIDED = 'Voided',
}

export type PayrollPaymentStatus = typeof EPayrollPaymentStatus[keyof typeof EPayrollPaymentStatus]


export enum EDocumentIssueStatus {
  RESOLVED = 'Resolved',
  REJECTED = 'Rejected',
  IN_REVIEW = 'In Review',
  OPEN = 'Open',
}
// 'upcoming-payrolls' | 'past-payrolls' | 'payslip-issues';
export enum EPayrollProcessingSubTab {
  UPCOMING_PAYROLLS = 'Upcoming Payrolls',
  PAST_PAYROLLS = 'Past Payrolls',
  PAYSLIP_ISSUES = 'Issues',
}


//////////////////////////////////////// ENUM END ////////////////////////////////////////


//////////////////////////////////////// INTERFACES DATA START ////////////////////////////////////////


// Retained for potential use or if BenefitsSection structure changes,
// but current BenefitsSection uses a different internal structure.
export interface Benefit {
  title: string;
  points: string[];
  image?: string;
  imageAlt?: string;
  animationDelay?: string;
}

// Types for Invoice functionality
export type InvoiceItem = SharedAccounting.InvoiceItem;


export type Client = SharedAccounting.Client;

export type InvoiceTerm = SharedAccounting.InvoiceTerm;

export type InvoicePayment = SharedAccounting.InvoicePayment;

export type PartialPayment = SharedAccounting.PartialPayment;

export type Invoice = SharedInvoice & {
    createdAt?: ISODate;
};

// Types for Time Tracking functionality
export type Project = SharedAccounting.Project;


export interface TimeEntry {
  id?: UUID;
  client?: Client; // Optional if not always linked directly or selected later
  project?: Project; // Optional
  description: string;
  startTime: Date;
  endTime: Date;
  durationSeconds: number;
  isBillable: boolean;
  hourlyRate?: number;
  status: TimeEntryStatus;
  hasBeenInvoiced: boolean;
  user: string; // For now, just a user name string
  projectId?: string;
  partyId?: string;
  productServiceId?: string;
  productService?: Product;
  invoiceId?: string;
  invoiceProductId?: string;
  employeeId?: UUID;
  dealerId?: UUID;
}

export interface TimeTrackingInvoiceSummary {
  totalInvoiced: number;
  outstanding: number;
  overdue: number;
  paid: number;
}


export interface TimeSheetGroup {
  id: string; // Composite key: clientId-year-month
  clientId: string;
  clientName: string;
  year: number;
  month: number; // 0-11
  monthLabel: string;
  totalSeconds: number;
  totalBillable: number;
  entryCount: number;
  entries: TimeEntry[];
}


// Types for Employee functionality
export type TaxBracket = SharedAccounting.TaxBracket;

export type PayrollFilingStatusType = SharedAccounting.PayrollFilingStatusType;

export type PayrollResidencyStatusType = SharedAccounting.PayrollResidencyStatusType;

export type PayrollTaxLayerType = SharedAccounting.PayrollTaxLayerType;

export type Employee = SharedAccounting.Employee;


export enum EPayRollAction {
  APPROVE = 'approve',
  REOPEN = 'reopen',
  DELETE = 'delete',
  REVIEW = 'review',
  PAY = 'pay',
  PROCESS = 'process'
}

// create a type from that 
export type PayrollAction = typeof EPayRollAction[keyof typeof EPayRollAction];

export interface Payroll {
  id: UUID;
  orgId: UUID;
  employeeId: UUID;
  cycleName: string;
  grossAmount: number;
  netSalary: number;
  payrollDate: string;
  periodEnd: string;
  periodStart: string;
  paymentFrequency: EPaymentFrequencyType;
  status: PayrollStatus;
  paymentStatus: EPayrollPaymentStatus;
  taxDeducted: number;

  currency?: string;
  exchangeRate?: number;
  /** FOR DISPLAY ONLY */
  period?: string;
  payslips?: Payslip[];

  // dates
  createdAt?: ISODate
  updatedAt?: ISODate
}

export interface Payslip {
  id: UUID;
  payrollId?: UUID;
  employeeId: UUID;
  employee?: {
    firstName: string;
    lastName: string;
    email: string;
    avatarUrl?: string;
    position?: string;
  };
  payroll?: {
    id: UUID;
    cycleName?: string;
    periodStart?: string;
    periodEnd?: string;
    payrollDate?: string;
    status?: PayrollStatus;
    paymentStatus?: EPayrollPaymentStatus;
    currency?: string;
  };
  period?: string;
  periodStart?: string;
  periodEnd?: string;
  payslipType?: 'regular' | 'arrears' | 'adjustment' | 'correction';
  sourcePayrollId?: UUID;
  grossPay: number;
  deductions: number;
  contributions: number;
  netPay: number;
  currency?: string;
  status: PayslipStatusType;
  paymentStatus?: EPayrollPaymentStatus;
  emailDeliveryStatus?: 'Sent' | 'Not Sent';
  emailSentAt?: string;
  pdfStatus?: 'Ready' | 'Missing';
  pdfUrl?: string;
  pdfStoragePath?: string;
  calculationSnapshot?: any;
  calculation_snapshot?: any;
  payDate?: string;
  paymentMethod?: PayslipPaymentMethod;
}


export interface TaxComplianceRecord {
  id: UUID;
  employee: {
    name: string;
    email: string;
    avatarUrl: string;
  };
  taxId: UUID;
  status: TaxComplianceStatus;
}


export interface Transaction {
  id: UUID;
  referenceId: UUID;
  date: ISODate; // ISO string
  time?: string; // e.g. '14:35 UTC'
  method: TransactionMethod;
  amount: number;
  currency: string;
  status: TransactionStatus;

  // Optional detailed fields for the modal
  transactionFee?: number;

  // Method-specific details
  bankDetails?: {
    senderAddress: string;
    receiverAddress: string;
    transactionReference: string;
  };
  stripeDetails?: {
    senderStripeId: UUID;
    receiverStripeMerchantId: UUID;
    transactionReference: string;
  };
  cryptoDetails?: {
    cryptoSent: number;
    cryptoCurrency: string; // e.g., 'BTC'
    conversionRate: number;
    fiatValue: number;
    networkFee: number;
    senderAddress: string;
    receiverAddress: string;
    transactionHash: string;
  };
}
export type PaymentTransaction = SharedAccounting.PaymentTransaction;


export interface PartialPaymentTransaction {
  id: UUID;
  transactionId: UUID;
  client: string;
  totalAmount: number;
  paid: number;
  remaining: number;
  dueDate: string; // ISO string
  status: PartialPaymentStatus;
}

export interface BNPLTransaction {
  id: UUID;
  transactionId: UUID;
  client: string;
  amount: number;
  bnplProvider: string;
  nextInstallment: string; // ISO string
  status: PartialPaymentStatus; // Reusing status type
}


// ExpenseTransaction is now defined near ExpenseSchema


export interface ConnectedBank {
  id: UUID;
  name: string;
  lastSync: string;
  status: BankSyncStatus;
  accountHolder: string;
  accountNumber: string;
  accountType: string;
  nextAutoSync: string;
}

export interface BankDetailRecentTransaction {
  date: string;
  description: string;
  amount: number;
  status: 'Posted' | 'Pending';
}


export type Supplier = SharedAccounting.Supplier;

// PurchaseOrderItem is now defined near PO schemas
export type Country = SharedAccounting.Country;

export type CountryState = SharedAccounting.CountryState;
export type City = SharedAccounting.City;

export type PaymentPreferences = SharedAccounting.PaymentPreferences;

export const KmstackABPartySchema = SharedKmstackABPartySchema;

export type KmstackABParty = SharedAccounting.KmstackABParty;

export type ExtendedKmstackABParty = SharedAccounting.ExtendedKmstackABParty;


export interface CryptoAsset {
  id: UUID;
  name: string;
  symbol: string;
  icon: React.ReactNode;
  balanceCrypto: number;
  balanceFiat: number;
}


export type CryptoTransaction = SharedAccounting.CryptoTransaction;

export type BankAccount = SharedAccounting.BankAccount;
export type PortalOverview = SharedAccounting.PortalOverview;
export type PortalTimeline = SharedAccounting.PortalTimeline;
export interface PaymentRequest {
  // ...
}
export type FinancialReport = SharedAccounting.FinancialReport;
export type DirectDebitInfo = SharedAccounting.DirectDebitInfo;
export type CryptoPaymentInfo = SharedAccounting.CryptoPaymentInfo;
export type CryptoPaymentStatus = SharedAccounting.CryptoPaymentStatus;
export type CheckoutSession = SharedAccounting.CheckoutSession;

export type PaymentMethod = SharedAccounting.PaymentMethod;

export type AuditLogEntry = SharedAccounting.AuditLogEntry;
//////////////////////////////////////// INTERFACES DATA END ////////////////////////////////////////


//////////////////////////////////////// INTERFACES UI PROPS START ////////////////////////////////////////

export interface NavLinkWithoutIcon extends Omit<NavLink, 'icon'> { }
export interface NavLinkWithoutHrefNIcon extends Omit<NavLink, 'icon' | 'href'> { }

// New type for Personalized Solutions cards
export interface SolutionCardProps {
  tag: string;
  title: string;
  description?: string;
  bgColor: string;
  textColor: string;
  graphicElement?: React.ReactNode;
}

export interface NavLink {
  name: string;
  span?: string;
  icon: JSX.Element;
  href: string;
  parentViewId?: DashboardView;
  visibleOnPlans?: readonly PlanType[];
  viewId?: DashboardView
  | TimeTrackingSubTab
  | EmployeeSubTabs
  | BareAutomationSubTabs
  | ClientSupplierSubTab
  | SettingsViewTabs
  | IntegrationSubTab
  | SecurityComplianceSubTab
  | ExpenseSubTab
  | InventoryTab
  | InventoryReportSubTab
  | InventoryManagementSubTabs
  | PayrollProcessingSubTab
  | PaymentViewSubTab
  | ReportCategory
  | "leave-approvals"
}
// Updated for 'Simple tools. Powerful outcomes.' section
export interface Feature {
  icon: React.ReactNode; // Can be a div placeholder or an actual SVG component (e.g. small circle)
  title: string;
  description: string;
  animationDelay?: string; // Still useful for staggered animations
}
export type NotificationState = {
  id: number;
  title: string;
  message: string;
  type: 'success' | 'loading' | 'error';
} | null;

export interface SelectionOption {
  value: string;
  label: string;
  description?: string;
}

export interface FieldConfig {
  label: string;
  name: string;
  type: string;
  required?: boolean;
  placeholder?: string;
  data?: string[]; // For native select/radio options
  selectOptions?: SelectionOption[]; // For custom select
  validation?: RegisterOptions; // Custom validation rules
  errorMessage?: string; // Custom error message
  render?: (props: any) => React.ReactNode; // Custom renderer
  renderColspan?: number;
  fullWidth?: boolean;
  /**
   * no need to pass this explicitly, it will be passed from the parent component
   * this is so that the parent component can control the number of columns
   */
  columns?: number;
  dependsOn?: string[];
  fetchOptions?: (dependencies: Record<string, any>) => Promise<SelectionOption[]>;
}

export interface ProductServiceFormProps {
  handleClose: () => void;
  resetState: () => void;
  onSave: (item: Product | Service, type: 'Product' | 'Service') => void;

  categoryDataOptions: SelectionOption[];
  taxRateLabels: SelectionOption[];
  formTaxOption: TaxOption[];

  inputClass: string;
  currentTaxOption: TaxOption;
  productService: Partial<Product>;
}
//////////////////////////////////////// INTERFACES UI PROPS END ////////////////////////////////////////

export const ExpenseSchema = SharedExpenseSchema;

export type ExpenseTransaction = SharedAccounting.ExpenseTransaction;


export type ReceiptProcessingJob = SharedAccounting.ReceiptProcessingJob;

export const ProductSchema = SharedProductSchema.omit({ updatedAt: true });

export type Product = SharedAccounting.Product;

export const BillItemSchema = SharedBillItemSchema;

export const BillSchema = SharedBillSchema;

export const FlattenedBillSchema = BillSchema.merge(BillItemSchema);

export const PurchaseOrderItemSchema = SharedPurchaseOrderItemSchema;

export type BillItem = SharedAccounting.BillItem;

export type PurchaseOrderItem = SharedAccounting.PurchaseOrderItem;

export const PurchaseOrderSchema = SharedPurchaseOrderSchema;

export const FlattenedPurchaseOrderSchema = PurchaseOrderSchema.merge(PurchaseOrderItemSchema);

export const PURCHASE_ORDER_HEADER_KEYS = PurchaseOrderSchema.keyof().options;
export const PURCHASE_ORDER_ITEM_KEYS = PurchaseOrderItemSchema.keyof().options;
// expenses
export const EXPENSE_HEADER_KEYS = ExpenseSchema.keyof().options;
// bills 
export const BILL_HEADER_KEYS = BillSchema.keyof().options;
export const BILL_ITEM_KEYS = BillItemSchema.keyof().options;

export type FlattenedPurchaseOrder = z.infer<typeof FlattenedPurchaseOrderSchema>;

export type PurchaseOrder = SharedAccounting.PurchaseOrder;

export const MODEL_SCHEMAS = {
  Product: ProductSchema,
  PurchaseOrder: FlattenedPurchaseOrderSchema,
  Expense: ExpenseSchema,
  Bill: FlattenedBillSchema,
  Party: KmstackABPartySchema,
} as const;

export const MODEL_KEYS = {
  Product: ProductSchema.keyof().options,
  PurchaseOrder: FlattenedPurchaseOrderSchema.keyof().options,
  Expense: ExpenseSchema.keyof().options,
  Bill: FlattenedBillSchema.keyof().options,
  Party: KmstackABPartySchema.keyof().options,
} as const;

export type ModelType = keyof typeof MODEL_KEYS;


// BILLS
export type Bill = SharedAccounting.Bill;


export type BillPayment = SharedAccounting.BillPayment;

export type BillAttachment = SharedAccounting.BillAttachment;

export type BillAuditEntry = SharedAccounting.BillAuditEntry;

export type ChartOfAccount = SharedAccounting.ChartOfAccount;

export type DashboardSummary = SharedAccounting.DashboardSummary;

export type FinancialReportsSummary = SharedAccounting.FinancialReportsSummary;

export type PinnedReport = SharedAccounting.PinnedReport;

export type AccountingPeriod = SharedAccounting.AccountingPeriod;

export type AccountingTrialBalanceLine = SharedAccounting.AccountingTrialBalanceLine;

export type AccountingTrialBalance = SharedAccounting.AccountingTrialBalance;


export type KmstackABVATBreakdown = SharedAccounting.KmstackABVATBreakdown;

export type VATTransactionLine = SharedAccounting.VATTransactionLine;

export type VATSummary = SharedAccounting.VATSummary;

export type SalesTaxSummary = SharedAccounting.SalesTaxSummary;

export type CorporateTaxSummary = SharedAccounting.CorporateTaxSummary;

export type TaxReportsSummary = SharedAccounting.TaxReportsSummary;

//////////////////////////////////////// INTERFACES BUSINESS LOGIC END ////////////////////////////////////////
export type TaxOption = SharedAccounting.TaxOption;

export type ProductService = Product | Service;

export type Service = Omit<SharedAccounting.Service, 'taxRate'>;


export type PaginationQuery = SharedAccounting.PaginationQuery;


// PAYROLL TYPES

export interface SalaryPolicy {
  id: string;
  orgId?: string;
  employeeId?: string;
  group?: string;
  name: string;
  increaseType: string;
  value: number;
  frequency: string;
  condition?: string;
  effectiveDate: string;
  isApplied?: boolean | null;
  status?: BaseGenericPolicyType | 'scheduled' | 'pending' | 'applied';
}

export enum JurisdictionLevel {
  NATIONAL = "national",
  FEDERAL = "federal",
  STATE = "state",
  REGIONAL = "regional",
  LOCAL = "local",
  AUTONOMOUS_COMMUNITY = "autonomous_community",
  FORAL = "foral",
  MUNICIPAL = "municipal",
  COMMUNAL = "communal",
  WAGE_TAX = "wage_tax",
  SOLIDARITY_SURCHARGE = "solidarity_surcharge",
  CHURCH = "church",
  LABOUR_MARKET = "labour_market",
  NATIONAL_FAMILY_QUOTIENT = "national_family_quotient",
  HIGH_INCOME_SURCHARGE = "high_income_surcharge",
}
export type JurisdictionLevelType = typeof JurisdictionLevel[keyof typeof JurisdictionLevel];

interface ValidDateRange {
  validFrom?: string | null;
  validTo?: string | null;
}

export interface TaxDeductionSettings extends ValidDateRange {
  rate: number;
  isProgressive: boolean;
  country: string;
  region?: string;
  jurisdictionLevel: JurisdictionLevelType;
  taxLayer?: PayrollTaxLayerType;
  filingStatus?: PayrollFilingStatusType | '';
  residencyStatus?: PayrollResidencyStatusType | '';
  brackets: TaxBracket[];
}

export interface TaxBracketPolicy extends ValidDateRange {
  id: string;
  country: string;
  region?: string;
  jurisdictionLevel?: JurisdictionLevelType;
  taxLayer?: PayrollTaxLayerType;
  filingStatus?: PayrollFilingStatusType | '';
  residencyStatus?: PayrollResidencyStatusType | '';
  orgId?: string;
  rangeFrom: number;
  rangeTo?: number;
  rate: number;
  fixedAmount?: number;
  description?: string;
  isEditable?: boolean;
  origin?: string;
  status?: BaseGenericPolicyType;
}

export interface PayrollJurisdictionPackMetadata {
  pack: 'usa' | 'eu_core' | 'eu_complex' | 'generic' | string;
  releaseScope: string;
  release_scope?: string;
  country?: string | null;
  allowedTaxLayers?: string[];
  allowedJurisdictionLevels?: string[];
  requiredEmployeeTaxFields?: string[];
  filingStatuses?: PayrollFilingStatusType[];
  residencyStatuses?: PayrollResidencyStatusType[];
  explicitZeroTaxRegions?: string[];
  assumptions?: string[];
  exclusions?: string[];
}

export interface PayrollTaxFormulaPolicy extends ValidDateRange {
  id: string;
  name: string;
  country: string;
  region?: string;
  jurisdictionLevel: JurisdictionLevelType;
  formulaType: string;
  parameters: Record<string, unknown>;
  description?: string;
  orgId?: string;
  isActive?: boolean | null;
  isEditable?: boolean | null;
  origin?: string;
  status?: BaseGenericPolicyType;
}

export interface PayrollTaxFormulaSettings extends ValidDateRange {
  name: string;
  country: string;
  region?: string;
  jurisdictionLevel: JurisdictionLevelType;
  formulaType: string;
  rate?: number | string;
  annualBaseTaxExemption?: number | string;
  familyParts?: number | string;
  formulaCode?: string;
  payrollTreatment?: string;
  accountingTreatment?: string;
  payslipLabel?: string;
  liabilityAccountId?: string;
  liabilityAccountName?: string;
  expenseAccountId?: string;
  expenseAccountName?: string;
  remittanceAuthority?: string;
  filingFrequency?: string;
  reviewerName?: string;
  description?: string;
  isActive?: boolean | null;
}

export interface TaxReliefPolicy extends ValidDateRange {
  id: string;
  name: string;
  country: string;
  orgId?: string;
  employeeId?: string;
  group?: string;
  type: string;
  value: number;
  applicableTo: string;
  isActive?: boolean | null;
  status?: BaseGenericPolicyType;
}

export interface LeavePolicy {
  id: string;
  country: string;
  orgId?: string;
  employeeId?: string;
  group?: string;
  leaveType: string;
  maxDaysPerYear: number;
  carryOverDays?: number | null;
  accrualRate?: number | null;
  publicHoliday?: number | null;
  publicHolidayRate?: number | null;
  workingDaysPerWeek?: number | null;
  eligibilityConfig?: Record<string, unknown> | null;
  totalEntitled?: number;
  usedDays?: number;
  availableDays?: number;
  accruedToDate?: number;
  status?: BaseGenericPolicyType;
}

export interface PayComponent {
  id: string;
  payslipPolicyId: string;
  name: string;
  code?: string | null;
  category: 'earning' | 'deduction' | 'contribution' | 'info';
  isMandatory?: boolean | null;
  calculationType: 'fixed' | 'percentage' | 'formula';
  value?: number | null;
  displayOrder?: number | null;
  employeeId?: string;
  group?: string;
  accountId?: UUID;
}

export interface PayslipPolicy {
  id: UUID;
  orgId: UUID;
  name: string;
  description?: string;
  defaultSalaryAccountId?: UUID;
  payrollLiabilityAccountId?: UUID;
  components: PayComponent[];
}

export enum ELeaveStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
}

export type LeaveStatus = typeof ELeaveStatus[keyof typeof ELeaveStatus];

export interface LeaveRecord {
  id: string;
  endDate: string;
  reason?: string;
  leaveType: string;
  startDate: string;
  daysTaken: number;
  employee_id: string;
  employeeName: string;
  employeePosition: string;
  status: LeaveStatus;
}

export interface FinancialDocumentIssue {
  id: string;
  orgId: UUID;
  documentType: string;
  documentId: UUID;
  raisedByUserId: UUID;
  employeeId: UUID;
  partyId?: UUID | null;
  issueType: string;
  message: string;
  status: DocumentIssueType;
  resolutionNote?: string | null;
  resolvedBy?: UUID | null;
  resolvedAt?: string | null;
  createdAt: string;
  updatedAt?: string | null;
}

export type DocumentIssue = FinancialDocumentIssue;

//////////////////////////////////////// INTERFACES BUSINESS LOGIC END ////////////////////////////////////////
